import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { faFilter, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { CheckboxComponent, FaIconComponent, InputComponent } from '@akostm/orbit-ui';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ConversationType } from '@clientwire/api';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConversationFilters } from '../../../interfaces';

export interface ConversationTypeController {
  type: ConversationType;
  controller: FormControl<boolean>;
}

@Component({
  selector: 'shared-conversation-list-search-bar',
  standalone: true,
  imports: [
    CommonModule,
    FaIconComponent,
    InputComponent,
    ReactiveFormsModule,
    CheckboxComponent,
    TranslatePipe,
  ],
  templateUrl: './conversation-list-search-bar.component.html',
  styleUrl: './conversation-list-search-bar.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationListSearchBarComponent {
  filters = input<ConversationFilters>({ search: '', showArchived: false, conversationTypes: [] });
  availableConversationTypes = input<ConversationType[]>([]);

  filtersChanged = output<ConversationFilters>();

  showFilters = signal(false);

  readonly searchIcon = faMagnifyingGlass;
  readonly filterIcon = faFilter;

  readonly #formBuilder = inject(FormBuilder);
  searchForm!: FormGroup;

  constructor() {
    this.initForm();
    this.subscribeToFormChanges();
    // React to changes in conversation types or filters
    effect(() => {
      const types = this.availableConversationTypes();
      const currentFilters = this.filters();

      // Update conversation type controllers when inputs change
      if (this.searchForm) {
        this.updateConversationTypeControllers(types, currentFilters.conversationTypes ?? []);
      }
    });

    // React to changes in filters from parent (store updates)
    effect(() => {
      const currentFilters = this.filters();

      // Update form controls if they exist, without emitting events to prevent loops
      if (this.searchForm) {
        this.searchInputController.setValue(currentFilters.search ?? '', { emitEvent: false });
        this.showArchivedController.setValue(currentFilters.showArchived ?? false, {
          emitEvent: false,
        });
      }
    });
  }

  toggleFilters(): void {
    this.showFilters.update((value) => !value);
  }

  onToggleFiltersKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleFilters();
    }
  }

  initForm(): void {
    // Create FormArray for conversation type checkboxes
    const currentFilters = this.filters();
    const activeTypeIds = currentFilters.conversationTypes ?? [];

    const conversationTypeArray = this.#formBuilder.array(
      this.availableConversationTypes().map((type) =>
        this.#formBuilder.group({
          type: [type],
          selected: new FormControl(activeTypeIds.includes(type.id), {
            nonNullable: true,
          }),
        })
      )
    );

    this.searchForm = this.#formBuilder.group({
      searchInputController: new FormControl(currentFilters.search ?? '', { nonNullable: true }),
      showArchivedController: new FormControl(currentFilters.showArchived ?? false, {
        nonNullable: true,
      }),
      conversationTypeControllers: conversationTypeArray,
    });
  }

  subscribeToFormChanges(): void {
    // Subscribe to search input changes with debounce
    this.searchInputController.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(() => {
        this.emitFilterChanges();
      });

    // Subscribe to showArchived changes
    this.showArchivedController.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.emitFilterChanges();
    });

    // Subscribe to conversation type checkbox changes
    this.conversationTypeControllersArray.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.emitFilterChanges();
    });
  }

  emitFilterChanges(): void {
    const selectedConversationTypes = this.conversationTypeControllers
      .filter((controller) => controller.controller.value)
      .map((controller) => controller.type.id);

    this.filtersChanged.emit({
      search: this.searchInputController.value,
      showArchived: this.showArchivedController.value,
      conversationTypes: selectedConversationTypes,
    });
  }

  updateConversationTypeControllers(types: ConversationType[], activeTypeIds: string[]): void {
    const currentArray = this.conversationTypeControllersArray;
    const currentTypes = currentArray.controls.map(
      (group) => (group.get('type')?.value as ConversationType).id
    );

    // Check if types have changed
    const typesChanged =
      types.length !== currentTypes.length ||
      !types.every((type) => currentTypes.includes(type.id));

    if (typesChanged) {
      // Clear and recreate the FormArray. Every clear()/push() would emit on valueChanges by
      // default, so a programmatic rebuild (e.g. when the conversation types finish loading)
      // would look like N+1 user filter changes and trigger a burst of identical requests.
      currentArray.clear({ emitEvent: false });
      types.forEach((type) => {
        currentArray.push(
          this.#formBuilder.group({
            type: [type],
            selected: new FormControl(activeTypeIds.includes(type.id), { nonNullable: true }),
          }),
          { emitEvent: false }
        );
      });
    } else {
      // Only update checkbox values if types haven't changed
      currentArray.controls.forEach((group) => {
        const type = group.get('type')?.value as ConversationType;
        const selectedControl = group.get('selected') as FormControl<boolean>;
        const isActive = activeTypeIds.includes(type.id);

        if (selectedControl.value !== isActive) {
          selectedControl.setValue(isActive, { emitEvent: false });
        }
      });
    }
  }

  get searchInputController() {
    return this.searchForm.get('searchInputController') as FormControl<string>;
  }

  get showArchivedController() {
    return this.searchForm.get('showArchivedController') as FormControl<boolean>;
  }

  get conversationTypeControllersArray() {
    return this.searchForm.get('conversationTypeControllers') as FormArray;
  }

  get conversationTypeControllers(): ConversationTypeController[] {
    return this.conversationTypeControllersArray.controls.map((group) => ({
      type: group.get('type')?.value as ConversationType,
      controller: group.get('selected') as FormControl<boolean>,
    }));
  }
}
